/**
 * Firebase Configuration - SLIK POS
 * This config is SHARED between staff app and customer portal
 * Security is handled by Firestore rules based on user role
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import {
    Firestore,
    enableNetwork,
    disableNetwork,
    initializeFirestore,
    memoryLocalCache
} from 'firebase/firestore';
import { getDatabase, Database } from 'firebase/database';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getAnalytics, Analytics } from 'firebase/analytics';

// Firebase configuration from environment variables
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Validate configuration
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.error('Firebase configuration is missing! Check your .env file.');
    console.error('Required: REACT_APP_FIREBASE_API_KEY, REACT_APP_FIREBASE_PROJECT_ID');
}

// Initialize Firebase App
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let realtimeDb: Database;
let storage: FirebaseStorage;
let analytics: Analytics | null = null;

try {
    app = initializeApp(firebaseConfig);

    // Initialize Authentication
    auth = getAuth(app);

    // 🔐 SHOP DEVICE LOGIN persistence (Jose 2026-08-09): keep the shop's Google-account Firebase
    // session signed in across reboots/app-reopens (browserLocalPersistence = IndexedDB). This is the
    // keystone of "barber PIN = single gate": once the iPad is signed into the shop account ONCE, it
    // stays the shop; on reboot only the barber PIN shows, no second login. Default is already LOCAL —
    // this makes it explicit + guaranteed. See project-slik-pos-shop-account-plan-2026-08-09.
    setPersistence(auth, browserLocalPersistence).catch(err =>
        console.error('[Auth] Failed to set local persistence:', err)
    );

    db = initializeFirestore(app, {
        // Drop undefined fields instead of throwing "Unsupported field value: undefined".
        // Without this, ANY form that sends an optional field as `undefined` (e.g. a barber
        // with no sliding-scale tier) fails the whole write. App-wide durability fix (2026-08-02).
        ignoreUndefinedProperties: true,
        // TRANSPORT AUTO-DETECT (2026-10-07): forcing long-polling (the 2026-09-24 fix for Safari/iOS
        // WebChannel wedging) itself wedged on networks that block/stall the long-poll transport — the
        // owner's staff app couldn't load its profile ("Connection problem") while the customer app
        // (default transport) worked on the SAME network. autoDetect probes and uses WebChannel when it
        // works, long-polling only when the network needs it — covers BOTH the Safari/iOS wedge AND
        // long-poll-hostile networks. (Staging-validated before prod.)
        experimentalAutoDetectLongPolling: true,
        // MEMORY CACHE, NOT PERSISTENT INDEXEDDB (Jose 2026-09-24): PROVEN by the incognito test —
        // a private window (fresh cache) logs in instantly while the normal window with the
        // persistent IndexedDB cache hangs the sign-in indefinitely. The persistentLocalCache /
        // multiTab IndexedDB store WEDGES on iOS/Safari (and contends with the app's writes), and a
        // wedged store never self-heals, so users get stuck needing incognito. Memory cache has no
        // IndexedDB to wedge — every load starts clean, reads go straight over long-polling. Trade
        // cross-reload offline caching (which a mostly-online POS doesn't rely on; critical data is
        // also mirrored in localStorage by the services) for a login that ALWAYS works.
        localCache: memoryLocalCache(),
    });

    // Initialize Realtime Database
    realtimeDb = getDatabase(app);

    // Initialize Storage
    storage = getStorage(app);

    // Initialize Analytics (only in browser)
    if (typeof window !== 'undefined' && process.env.REACT_APP_ENABLE_ANALYTICS === 'true') {
        analytics = getAnalytics(app);
    }

} catch (error) {
    console.error('Firebase initialization error:', error);
    throw error;
}

// Connection state management
export const enableOfflineMode = async (): Promise<void> => {
    try {
        await disableNetwork(db);
    } catch (error) {
        console.error('Error enabling offline mode:', error);
    }
};

export const enableOnlineMode = async (): Promise<void> => {
    try {
        await enableNetwork(db);
    } catch (error) {
        console.error('Error enabling online mode:', error);
    }
};

// Export initialized services
export { app, auth, db, realtimeDb, storage, analytics };

// Export default for convenience
export default app;

/**
 * Firebase Collections Structure
 *
 * /users/{userId}
 *   - email, role ('staff' | 'customer'), createdAt, lastLogin
 *
 * /clients/{clientId}
 *   - firstName, lastName, phone, email, preferredBarber, cpi, userId
 *
 * /barbers/{barberId}
 *   - name, avatar, rating, specialties, schedule, isActive
 *
 * /services/{serviceId}
 *   - name, duration, price, category, description
 *
 * /appointments/{appointmentId}
 *   - clientId, barberId, service, date, time, status, source
 *
 * /emergencyBookings/{bookingId}
 *   - clientId, urgencyLevel, status, emergencyFee, createdAt
 */
